import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchCustomerDetails, getRacketFreeEligibility, getCustomerProfile } from "../../api";
import Pagination from "../../components/Pagination/Pagination";
import styles from "./FreeStringingServices.module.css";

const FreeStringingServices = () => {
  const [freeServices, setFreeServices] = useState(0);
  const [usedServices, setUsedServices] = useState(0);
  const [freeServicesTotal, setFreeServicesTotal] = useState(0);
  const [serviceClaims, setServiceClaims] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [eligibilityMap, setEligibilityMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [eligLoading, setEligLoading] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [customerId, setCustomerId] = useState(null);

  const itemsPerPage = 5;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("Authentication token not found.");

        const [detailsResponse, profileResponse] = await Promise.all([
          fetchCustomerDetails(token),
          getCustomerProfile()
        ]);

        if (detailsResponse.data) {
          setServiceClaims(detailsResponse.data.service_claims || []);
          setPurchases(detailsResponse.data.purchases || []);
          const free = detailsResponse.data.free_stringing_services || 0;
          const used = parseInt(detailsResponse.data.used_services, 10) || 0;
          setFreeServices(free);
          setUsedServices(used);
          setFreeServicesTotal(free + used);
        }

        if (profileResponse.data) {
          setCustomerId(profileResponse.data.customer_id);
        }
      } catch (error) {
        console.error("Error fetching free services:", error);
        setError(error.message || "Failed to fetch free services.");
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  // fetch monthly eligibility per racket
  useEffect(() => {
    const loadEligibility = async () => {
      if (!serviceClaims?.length) {
        setEligibilityMap({});
        return;
      }
      setEligLoading(true);
      try {
        const uniqueRpIds = [
          ...new Set(serviceClaims.map((c) => c.rp_id).filter(Boolean)),
        ];
        const entries = await Promise.all(
          uniqueRpIds.map(async (rpId) => {
            try {
              const data = await getRacketFreeEligibility(rpId);
              return [rpId, data];
            } catch {
              return [rpId, { eligible: true, nextEligibleDate: null }];
            }
          })
        );
        setEligibilityMap(Object.fromEntries(entries));
      } finally {
        setEligLoading(false);
      }
    };
    loadEligibility();
  }, [serviceClaims]);

  const totalPages = Math.ceil(serviceClaims.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedClaims = serviceClaims.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handleBookingClick = (
    racketName,
    isFreeFinal,
    rp_id,
    claim_id,
    nextEligibleDate
  ) => {
    navigate("/customer/dashboard/booking-form", {
      state: {
        customerId,
        freeServices,
        usedServices,
        freeServicesTotal,
        racketName,
        isFree: isFreeFinal,
        rp_id,
        claim_id,
        nextEligibleDate,
      },
    });
  };

  return (
    <div className={styles.purchaseListContainer}>
      <h2>Stringing Services</h2>

      {loading ? (
        <div className={styles.spinnerContainer}>
          <div className={styles.spinner}></div>
        </div>
      ) : error ? (
        <p className={styles.error}>{error}</p>
      ) : (
        <div>
          {freeServices > 0 ? (
            <p className={styles.serviceMessage}>
              Dear Customer, you still have <strong>{freeServices}</strong> free
              stringing
              {freeServices === 1 ? " service" : " services"} available.
              <br />
              <small>
                (Please note: the <strong>stringing service</strong> is free,
                but the strings themselves are not included.)
              </small>
            </p>
          ) : (
            <p className={styles.serviceMessage}>
              Dear Customer, you currently don’t have any free stringing
              services left. You can still book a{" "}
              <strong>paid stringing service</strong> anytime.
            </p>
          )}

          <table className={styles.purchaseTable}>
            <thead>
              <tr>
                <th>Racket Name</th>
                <th>Total Free Services</th>
                <th>Used Services</th>
                <th>Valid Until</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {displayedClaims.length > 0 ? (
                displayedClaims.map((claim, index) => {
                  const purchase = purchases.find(
                    (p) => p.rp_id === claim.rp_id
                  );
                  const racketName = purchase?.product_name || "Unknown Racket";

                  const total = claim.total_free_services ?? 0;
                  const used = claim.used_services ?? 0;
                  const remaining = total - used;
                  const hasUnitsRemaining = remaining > 0;

                  const elig = eligibilityMap[claim.rp_id];
                  const monthlyEligible = elig ? !!elig.eligible : true;
                  const nextDate = elig?.nextEligibleDate || null;

                  // final decision: free only if units remain AND this racket is not in cooldown
                  const isFreeFinal = hasUnitsRemaining && monthlyEligible;

                  const btnClass = isFreeFinal
                    ? styles.redeemButton
                    : styles.paidBookingButton;
                  const btnLabel = isFreeFinal ? "Redeem Free" : "Book Paid";

                  return (
                    <tr
                      key={index}
                      data-status={hasUnitsRemaining ? "active" : "expired"}
                    >
                      <td>{racketName}</td>
                      <td>{total}</td>
                      <td>{used}</td>
                      <td>
                        {new Date(claim.valid_until).toLocaleDateString(
                          "en-GB"
                        )}
                      </td>
                      <td
                        data-remaining={remaining}
                        className={styles.actionCell}
                      >
                        <div className={styles.actionStack}>
                          <button
                            className={btnClass}
                            onClick={() =>
                              handleBookingClick(
                                racketName,
                                isFreeFinal,
                                claim.rp_id,
                                claim.claim_id,
                                nextDate
                              )
                            }
                            title={
                              hasUnitsRemaining && !isFreeFinal && nextDate
                                ? `Next free on ${nextDate}`
                                : undefined
                            }
                          >
                            {btnLabel}
                          </button>

                          {hasUnitsRemaining && !isFreeFinal && (
                            <div className={styles.cooldownNote}>
                              Next free stringing for this racket will be
                              available after one month
                              {nextDate ? ` (on ${nextDate})` : ""}.
                            </div>
                          )}

                          {eligLoading && hasUnitsRemaining && (
                            <div className={styles.hintText}>
                              Checking eligibility…
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className={styles.emptyMessage}>
                    No free services available.
                  </td>
                </tr>
              )}

              <tr>
                <td colSpan="5" className={styles.otherRacketRow}>
                  <strong>Need stringing for a different racket?</strong>
                  <button
                    className={styles.paidBookingButton}
                    onClick={() => handleBookingClick("", false)}
                  >
                    Book it here
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          {serviceClaims.length > itemsPerPage && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default FreeStringingServices;
